// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "../primitives/I18n.res.js";
import * as Json from "../primitives/Json.res.js";
import * as React from "react";
import * as Button from "../resources/actions/Button.res.js";
import * as Future from "rescript-future/src/Future.res.js";
import * as Inline from "../resources/layout-and-structure/Inline.res.js";
import * as AsyncData from "../primitives/AsyncData.res.js";
import * as NextBanner from "../resources/feedback-indicators/NextBanner.res.js";
import * as FuturePromise from "rescript-future/src/FuturePromise.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as InputTextField from "../resources/selection-and-input/InputTextField.res.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as CompanyLegalNumber from "./CompanyLegalNumber.res.js";
import * as CompanyRequestRegistry from "./CompanyRequestRegistry.res.js";

let i18nPrefix = "siret_input_field.legal_form.";

function SiretInputField$LegalFormBanner(props) {
  let legalForm = props.legalForm;
  if (typeof legalForm === "object") {
    if (legalForm.privacyRight) {
      return JsxRuntime.jsxs(NextBanner.make, {
        children: [
          JsxRuntime.jsx(NextBanner.Text.make, {
            text: I18n.t(i18nPrefix + "privacy_right.text")
          }),
          JsxRuntime.jsx(NextBanner.Gap.make, {
            space: "xsmall"
          }),
          JsxRuntime.jsx(NextBanner.Text.make, {
            text: I18n.t(i18nPrefix + "privacy_right.example_one"),
            bold: true
          }),
          JsxRuntime.jsx(NextBanner.Gap.make, {}),
          JsxRuntime.jsx(NextBanner.Text.make, {
            text: I18n.t(i18nPrefix + "privacy_right.example_two"),
            bold: true
          }),
          JsxRuntime.jsx(NextBanner.Gap.make, {}),
          JsxRuntime.jsx(NextBanner.Text.make, {
            text: I18n.t(i18nPrefix + "privacy_right.example_three"),
            bold: true
          })
        ],
        variation: "warning",
        title: I18n.t(i18nPrefix + "privacy_right.title")
      });
    } else {
      return JsxRuntime.jsx(NextBanner.make, {
        children: JsxRuntime.jsx(NextBanner.Text.make, {
          text: I18n.t(i18nPrefix + "sole_trader.text")
        }),
        variation: "info",
        title: I18n.t(i18nPrefix + "sole_trader.title")
      });
    }
  } else {
    return null;
  }
}

let LegalFormBanner = {
  i18nPrefix: i18nPrefix,
  make: SiretInputField$LegalFormBanner
};

function endpoint(siret) {
  return "https://recherche-entreprises.api.gouv.fr/search?q=" + siret;
}

let nonDiffusible = "[NON-DIFFUSIBLE]";

function sanitize(value) {
  if (value !== undefined && value !== nonDiffusible) {
    return value;
  } else {
    return "";
  }
}

function decode(siret, json) {
  let firstResult = Stdlib_Option.flatMap(Stdlib_Option.flatMap(Json.flatDecodeDictFieldArray(Json.decodeDict(json), "results"), results => results[0]), Json.decodeDict);
  let match = Stdlib_Option.orElse(Json.flatDecodeDictFieldString(firstResult, "nom_raison_sociale"), Json.flatDecodeDictFieldString(firstResult, "nom_complet"));
  if (match === undefined) {
    return;
  }
  let matchingEstablishment = Stdlib_Option.flatMap(Json.flatDecodeDictFieldArray(firstResult, "matching_etablissements"), establishments => Stdlib_Option.flatMap(establishments.find(establishment => {
    let establishmentSiret = Json.flatDecodeDictFieldString(Json.decodeDict(establishment), "siret");
    if (establishmentSiret !== undefined) {
      return establishmentSiret === siret;
    } else {
      return false;
    }
  }), Json.decodeDict));
  let addressDict = Stdlib_Option.orElse(matchingEstablishment, Json.flatDecodeDictFieldDict(firstResult, "siege"));
  let rawCorporateName = Json.flatDecodeDictFieldString(firstResult, "nom_raison_sociale");
  let rawFullName = Json.flatDecodeDictFieldString(firstResult, "nom_complet");
  let rawFullAddress = Json.flatDecodeDictFieldString(addressDict, "adresse");
  let rawPostalCode = Json.flatDecodeDictFieldString(addressDict, "code_postal");
  let director = CompanyRequestRegistry.decodeDirector(firstResult);
  let soleProprietorship = Stdlib_Option.getOr(Json.flatDecodeDictFieldBoolean(Json.flatDecodeDictFieldDict(firstResult, "complements"), "est_entrepreneur_individuel"), false);
  let privacyRight = soleProprietorship && [
    rawFullName,
    rawFullAddress,
    rawPostalCode
  ].some(value => value === nonDiffusible);
  let corporateName = sanitize(rawCorporateName);
  let corporateName$1;
  if (corporateName === "") {
    let fullName = sanitize(rawFullName);
    corporateName$1 = fullName === "" ? "" : (
        soleProprietorship ? "EI " + fullName : fullName
      );
  } else {
    corporateName$1 = corporateName;
  }
  return {
    legalForm: soleProprietorship ? ({
        TAG: "NaturalPerson",
        privacyRight: privacyRight
      }) : "LegalEntity",
    company: privacyRight ? ({
        corporateName: "",
        fullAddress: "",
        street: "",
        city: "",
        postalCode: "",
        vatNumber: "",
        apeNafCode: "",
        directorFirstName: "",
        directorLastName: ""
      }) : ({
        corporateName: corporateName$1,
        fullAddress: sanitize(rawFullAddress),
        street: CompanyRequestRegistry.decodeStreet(addressDict),
        city: sanitize(Json.flatDecodeDictFieldString(addressDict, "libelle_commune")),
        postalCode: sanitize(rawPostalCode),
        vatNumber: Stdlib_Option.getOr(Stdlib_Option.flatMap(Stdlib_Option.flatMap(Json.flatDecodeDictFieldArray(firstResult, "tva"), tvaNumbers => tvaNumbers[0]), Json.decodeString), ""),
        apeNafCode: Stdlib_Option.getOr(Json.flatDecodeDictFieldString(firstResult, "activite_principale"), ""),
        directorFirstName: Stdlib_Option.getOr(Stdlib_Option.map(director, param => param[0]), ""),
        directorLastName: Stdlib_Option.getOr(Stdlib_Option.map(director, param => param[1]), "")
      })
  };
}

function make(siret) {
  return Future.map(FuturePromise.fromPromise(fetch("https://recherche-entreprises.api.gouv.fr/search?q=" + siret, {
    method: "GET"
  }).then(response => {
    if (response.ok) {
      return response.json().then(json => Primitive_option.some(decode(siret, json)));
    } else {
      return Promise.resolve(undefined);
    }
  })), undefined, result => {
    if (result.TAG !== "Ok") {
      return {
        TAG: "Error",
        _0: undefined
      };
    }
    let company = result._0;
    if (company !== undefined) {
      return {
        TAG: "Ok",
        _0: Primitive_option.valFromOption(company)
      };
    } else {
      return {
        TAG: "Error",
        _0: undefined
      };
    }
  });
}

let CompanyLookupRequest = {
  endpoint: endpoint,
  nonDiffusible: nonDiffusible,
  sanitize: sanitize,
  decode: decode,
  make: make
};

function sanitize$1(value) {
  return value.replace(/\s/g, "");
}

function SiretInputField(props) {
  let onBlur = props.onBlur;
  let onCompanyFound = props.onCompanyFound;
  let onChange = props.onChange;
  let __required = props.required;
  let __placeholder = props.placeholder;
  let __label = props.label;
  let __focused = props.focused;
  let __errored = props.errored;
  let __disabled = props.disabled;
  let value = props.value;
  let disabled = __disabled !== undefined ? __disabled : false;
  let errored = __errored !== undefined ? __errored : false;
  let focused = __focused !== undefined ? __focused : false;
  let label = __label !== undefined ? __label : I18n.t("siret_input_field.label");
  let placeholder = __placeholder !== undefined ? __placeholder : I18n.t("siret_input_field.placeholder");
  let required = __required !== undefined ? __required : false;
  let match = React.useState(() => "NotAsked");
  let setLookupResult = match[1];
  let lookupResult = match[0];
  let handleBlur = () => {
    let sanitized = sanitize$1(value);
    if (sanitized !== value) {
      onChange(sanitized);
    }
    Stdlib_Option.forEach(onBlur, onBlur => onBlur());
  };
  React.useEffect(() => {
    if (CompanyLegalNumber.French.isValidSiret(value)) {
      setLookupResult(prev => {
        if (prev === "NotAsked") {
          return "Loading";
        } else {
          return AsyncData.toBusy(prev);
        }
      });
      let future = make(value);
      Future.get(future, result => {
        if (result.TAG !== "Ok") {
          return;
        }
        let lookup = result._0;
        if (lookup !== undefined) {
          return setLookupResult(param => ({
            TAG: "Done",
            _0: {
              TAG: "Ok",
              _0: lookup
            }
          }));
        } else {
          return setLookupResult(param => ({
            TAG: "Done",
            _0: {
              TAG: "Error",
              _0: undefined
            }
          }));
        }
      });
      return () => Future.cancel(future);
    }
    setLookupResult(param => "NotAsked");
  }, [value]);
  React.useEffect(() => {
    if (onCompanyFound !== undefined && typeof lookupResult === "object" && lookupResult.TAG === "Done") {
      let lookup = lookupResult._0;
      if (lookup.TAG === "Ok") {
        onCompanyFound(lookup._0);
      } else {
        onCompanyFound(undefined);
      }
    }
  }, [lookupResult]);
  let errorMessage = errored ? Stdlib_Option.getOr(props.errorMessage, I18n.t("siret_input_field.error_message")) : undefined;
  let tmp;
  if (AsyncData.isNotAsked(lookupResult)) {
    tmp = null;
  } else {
    let newrecord = {...({
      inert: "true"
    })};
    let tmp$1;
    tmp$1 = typeof lookupResult !== "object" ? undefined : (
        lookupResult.TAG === "Reloading" ? (
            lookupResult._0.TAG === "Ok" ? undefined : I18n.t("siret_input_field.verification_status.invalid")
          ) : (
            lookupResult._0.TAG === "Ok" ? undefined : I18n.t("siret_input_field.verification_status.invalid")
          )
      );
    let tmp$2;
    tmp$2 = typeof lookupResult !== "object" ? ({
        NAME: "neutral",
        VAL: {
          quiet: true
        }
      }) : (
        lookupResult.TAG === "Reloading" ? (
            lookupResult._0.TAG === "Ok" ? "success" : "danger"
          ) : (
            lookupResult._0.TAG === "Ok" ? "success" : "danger"
          )
      );
    let tmp$3;
    tmp$3 = typeof lookupResult !== "object" ? "blank-placeholder" : (
        lookupResult.TAG === "Reloading" ? (
            lookupResult._0.TAG === "Ok" ? "status_ok" : "status_nok"
          ) : (
            lookupResult._0.TAG === "Ok" ? "status_ok" : "status_nok"
          )
      );
    newrecord.children = JsxRuntime.jsx(Button.make, {
      label: tmp$1,
      variant: {
        NAME: "ghost",
        VAL: {
          bordered: true
        }
      },
      tone: tmp$2,
      pending: AsyncData.isBusy(lookupResult),
      icon: tmp$3,
      stylex: {
        marginTop: "22.5px"
      }
    });
    tmp = JsxRuntime.jsx("div", newrecord);
  }
  return JsxRuntime.jsxs(Inline.make, {
    children: [
      JsxRuntime.jsx(InputTextField.make, {
        label: label,
        required: required,
        disabled: disabled,
        focused: focused,
        placeholder: placeholder,
        autoTrim: false,
        errorMessage: errorMessage,
        value: value,
        onChange: onChange,
        onFocus: props.onFocus,
        onBlur: handleBlur
      }),
      tmp
    ],
    space: "small",
    alignY: "top",
    grow: true
  });
}

let make$1 = SiretInputField;

export {
  LegalFormBanner,
  CompanyLookupRequest,
  make$1 as make,
}
/* I18n Not a pure module */
